/**
 * Demo data cleanup for the Operator Panel Scheduled segment of the Welcome Tour.
 *
 * The Scheduled segment shares `inject: injectOperatorPanelDemo` with the OP segment
 * so the conductor skips cleanup between them (avoids intermediate empty state that
 * triggers OperatorPanel's useFocusEffect → navigateBack → closes the modal).
 *
 * Scheduled-specific data (operationsSearch, isWithERP, statStorage) is injected
 * by `getOperatorPanelScheduledParams()` in tour.ts right before navigation.
 *
 * This cleanup handles everything: OP base data + scheduled-specific data.
 */

import { AppThunk } from '@store/types';
import { setIsWithERP } from '@reducers/membership';
import { setStatStorage } from '@reducers/search';
import { cleanupOperatorPanelDemo } from './operator-panel';

// Saved before tour modifies it — restored on cleanup
let originalIsWithERP: boolean | undefined;

/**
 * Save the original isWithERP value before the tour sets it to true.
 * Called from getOperatorPanelScheduledParams() in tour.ts.
 */
export const saveOriginalIsWithERP = (value: boolean | undefined): void => {
  originalIsWithERP = value;
};

/**
 * Cleanup all demo data after the Scheduled segment ends.
 * Restores scheduled-specific state, then delegates to OP cleanup for base data.
 */
export const cleanupOperatorPanelScheduledDemo = (): AppThunk => (dispatch) => {
  // Delegate to OP cleanup (clears registry, tourActive, demo machine, batch, connector)
  dispatch(cleanupOperatorPanelDemo());

  // Restore isWithERP to the value it had before the tour
  dispatch(setIsWithERP(originalIsWithERP ?? false));

  // Clear stat storage values we pre-populated (reset to 0)
  for (const key of ['OPERATIONS_NOW', 'OPERATIONS_LATE', 'OPERATIONS_NEXT_WEEK', 'OPERATIONS_AFTER_NEXT_WEEK']) {
    dispatch(setStatStorage({ name: key, value: 0 }));
  }
};
